import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Send, X, MessageCircle, Sparkles, Building2, Minimize2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import ContactModal from "../components/ContactModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BOT_AVATAR = "https://customer-assets.emergentagent.com/job_job-chatbot-1/artifacts/ab0dslhp_L%C3%A4chelndes%20Portrait%20einer%20jungen%20Frau.png";

const TypingIndicator = () => (
  <div className="flex items-start gap-3 animate-fade-in">
    <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-md">
      <img src={BOT_AVATAR} alt="Bot" className="w-full h-full object-cover" />
    </div>
    <div className="bg-white border border-slate-100 rounded-2xl rounded-tl-sm shadow-sm px-3 py-2">
      <div className="flex gap-1">
        <span className="typing-dot w-2 h-2 bg-slate-400 rounded-full"></span>
        <span className="typing-dot w-2 h-2 bg-slate-400 rounded-full"></span>
        <span className="typing-dot w-2 h-2 bg-slate-400 rounded-full"></span>
      </div>
    </div>
  </div>
);

const ChatMessage = ({ message, index }) => {
  const isBot = message.role === "assistant";
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`flex items-start gap-2 ${isBot ? "" : "flex-row-reverse"}`}
      data-testid={`chat-message-${message.role}-${index}`}
    >
      {isBot && (
        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-md">
          <img src={BOT_AVATAR} alt="Bot" className="w-full h-full object-cover" />
        </div>
      )}
      <div
        className={`max-w-[80%] px-3 py-2 shadow-sm text-sm ${
          isBot
            ? "bg-white border border-slate-100 text-slate-800 rounded-2xl rounded-tl-sm"
            : "bg-gradient-to-br from-blue-600 to-violet-600 text-white rounded-2xl rounded-tr-sm"
        }`}
      >
        <p className="leading-relaxed whitespace-pre-wrap">{message.content}</p>
      </div>
    </motion.div>
  );
};

export default function EmbedChatPage() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showContactButton, setShowContactButton] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const scrollAreaRef = useRef(null);
  const inputRef = useRef(null);

  // Check URL params for auto-open
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('open') === 'true') {
      setIsOpen(true);
    }
  }, []);

  // Initialize chat when opened
  useEffect(() => {
    if (isOpen && !isInitialized) {
      initializeChat();
    }
  }, [isOpen, isInitialized]);

  // Auto-scroll when messages change - show BEGINNING of new message
  useEffect(() => {
    if (scrollAreaRef.current && messages.length > 0) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        requestAnimationFrame(() => {
          const messageElements = scrollContainer.querySelectorAll('[data-testid^="chat-message-"]');
          const lastMessage = messageElements[messageElements.length - 1];
          
          if (lastMessage) {
            const scrollTop = lastMessage.offsetTop - 16;
            scrollContainer.scrollTo({
              top: scrollTop,
              behavior: 'smooth'
            });
          } else {
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
          }
        });
      }
    }
  }, [messages, isLoading]);

  // Separate effect for button - scroll to bottom when button appears
  useEffect(() => {
    if (showContactButton && scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        setTimeout(() => {
          scrollContainer.scrollTo({
            top: scrollContainer.scrollHeight,
            behavior: 'smooth'
          });
        }, 300);
      }
    }
  }, [showContactButton]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const initializeChat = async () => {
    try {
      const sessionResponse = await axios.post(`${API}/chat/session`);
      const newSessionId = sessionResponse.data.session_id;
      setSessionId(newSessionId);
      
      setMessages([{
        role: "assistant",
        content: "Hey 👋 ich bin Lea von Factory-Hype!\n\nCool, dass du hier bist! Du bist wegen unseres Jobs hier – wir checken jetzt kurz, ob das für dich passt. Dauert keine Minute.\n\nFalls du vorab eine Frage hast, schreib sie gern – ansonsten: Wie alt bist du?"
      }]);
      setIsInitialized(true);
    } catch (error) {
      console.error("Error initializing chat:", error);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!inputValue.trim() || isLoading || !sessionId || isSubmitted) return;

    const userMessage = inputValue.trim();
    setInputValue("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: userMessage
      });
      
      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
      
      if (response.data.show_contact_button) {
        setShowContactButton(true);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Entschuldigung, es gab ein technisches Problem. Bitte versuche es erneut." 
      }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleContactSuccess = () => {
    setIsContactModalOpen(false);
    setIsSubmitted(true);
    setMessages(prev => [...prev, {
      role: "assistant",
      content: "Danke dir! 🙌\n\nWir haben deine Daten erhalten und melden uns zeitnah telefonisch bei dir. Bis bald!"
    }]);
  };

  return (
    <div className="fixed inset-0 flex items-end justify-end p-4 pointer-events-none" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Chat Toggle Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => setIsOpen(true)}
            className="pointer-events-auto w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-violet-600 text-white shadow-lg shadow-blue-500/30 flex items-center justify-center hover:scale-110 transition-transform"
            data-testid="chat-toggle-button"
          >
            <MessageCircle className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="pointer-events-auto w-full max-w-[400px] h-[600px] max-h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200"
            data-testid="embed-chat-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-violet-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <Building2 className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-bold text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Hype-Factory</h2>
                  <p className="text-xs text-white/80">Bewerber-Chat</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                data-testid="close-chat-button"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <ScrollArea ref={scrollAreaRef} className="flex-1 px-4">
              <div className="py-4 space-y-3">
                {messages.map((msg, idx) => (
                  <ChatMessage key={idx} message={msg} index={idx} />
                ))}
                
                {isLoading && <TypingIndicator />}
                
                {/* Contact Button - Inside scroll area */}
                {showContactButton && !isSubmitted && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex justify-center pt-2 pb-4"
                  >
                    <Button
                      onClick={() => setIsContactModalOpen(true)}
                      className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white rounded-full px-6 py-5 text-sm font-semibold shadow-lg shadow-blue-500/30 animate-pulse"
                      data-testid="contact-button"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      ✨ Kontaktdaten eintragen ✨
                    </Button>
                  </motion.div>
                )}
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="p-3 border-t border-slate-100 bg-slate-50">
              <form onSubmit={sendMessage} className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={isSubmitted ? "Chat beendet" : "Nachricht schreiben..."}
                  disabled={isLoading || isSubmitted}
                  className="flex-1 rounded-full border-slate-200 bg-white px-4 py-5 text-sm"
                  data-testid="chat-input"
                />
                <Button
                  type="submit"
                  disabled={!inputValue.trim() || isLoading || isSubmitted}
                  className="rounded-full w-10 h-10 bg-gradient-to-br from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 shadow-lg shadow-blue-500/30 p-0"
                  data-testid="send-button"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Contact Modal */}
      <div className="pointer-events-auto">
        <ContactModal
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
          onSuccess={handleContactSuccess}
          sessionId={sessionId}
        />
      </div>
    </div>
  );
}
